import { useEffect, useState } from 'react';
import TicketPreprocessingPage from './ticket-preprocessing';
import { TabsContent } from '@/components/ui/tabs';
import { toast } from '@/components/ui/sonner';
import { useBrandSettings, useSetAutomationGuards } from '@/hooks/use-brand-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { MAX_HOLD_MESSAGE_DELAY_SECONDS, MAX_HOLD_MESSAGE_TEXT_CHARS, MIN_HOLD_MESSAGE_DELAY_SECONDS } from '@/lib/automation-guards';
import { getApiErrorMessage } from '@/lib/utils';
import {
	CommitNumberInput,
	Field,
	SaveBar,
	SETTINGS_INPUT_CLASS,
	SettingsBody,
	SettingsCard,
	SettingsCardSkeleton,
	SettingsError,
	SettingsPage,
	SettingsSection,
	ToggleRow,
	useTabParam,
	type SettingsTab
} from '@/modules/settings';
import type { AutomationGuards } from '@/types';

type HandlingTab = 'pre-processing' | 'customer-facing';

const TABS: SettingsTab<HandlingTab>[] = [
	{ value: 'pre-processing', label: 'Pre-Processing' },
	{ value: 'customer-facing', label: 'Customer Facing' }
];

type HoldMessage = AutomationGuards['holdMessage'];

/** Handling › Customer Facing: the live-chat hold message (an automation guard, saved on its own). */
function CustomerFacingTab() {
	const { data: brandData, isLoading, isError } = useBrandSettings();
	const setAutomationGuards = useSetAutomationGuards();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');
	const saved = brandData?.automationGuards.holdMessage;
	const [hold, setHold] = useState<HoldMessage | undefined>(saved);

	useEffect(() => setHold(saved), [saved]);

	const isDirty = Boolean(hold && saved) && JSON.stringify(hold) !== JSON.stringify(saved);
	const disabled = !canEdit || setAutomationGuards.isPending;

	const handleSave = async () => {
		if (!brandData || !hold) return;
		try {
			// Only the hold message changes; the other guards go back exactly as the server has them.
			await setAutomationGuards.mutateAsync({ ...brandData.automationGuards, holdMessage: { ...hold, text: hold.text.trim() } });
			toast.success('Hold message saved');
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to save the hold message. Please try again.'));
		}
	};

	return (
		<>
			<SettingsBody>
				<SettingsSection label='Customer-facing messages'>
					{isError ? (
						<SettingsError />
					) : isLoading || !hold ? (
						<SettingsCardSkeleton rows={2} />
					) : (
						<SettingsCard className='gap-6'>
							<ToggleRow
								title='Hold message'
								description={`Live chat only. If Kim is slow to answer, send the customer a short "looking into this" message first. Email, SMS and social replies never get one.`}
								checked={hold.enabled}
								disabled={disabled}
								onCheckedChange={(enabled) => setHold({ ...hold, enabled })}
							/>
							{hold.enabled && (
								<>
									<div className='flex items-center justify-between gap-4'>
										<p className='text-[13px] font-medium leading-[18px] text-ink'>Send it after</p>
										<div className='flex items-center gap-2 text-[13px] text-gray-900'>
											<CommitNumberInput
												aria-label='Hold message delay in seconds'
												value={hold.delaySeconds}
												min={MIN_HOLD_MESSAGE_DELAY_SECONDS}
												max={MAX_HOLD_MESSAGE_DELAY_SECONDS}
												disabled={disabled}
												onCommit={(delaySeconds) => setHold({ ...hold, delaySeconds })}
											/>
											seconds without an answer
										</div>
									</div>
									<Field
										label='Hold message'
										htmlFor='hold-message-text'
										hint={`Leave empty and Kim writes a fresh line for each chat. One line, up to ${MAX_HOLD_MESSAGE_TEXT_CHARS} characters.`}
									>
										<input
											id='hold-message-text'
											type='text'
											maxLength={MAX_HOLD_MESSAGE_TEXT_CHARS}
											placeholder='Leave empty and Kim writes a line for each message'
											disabled={disabled}
											value={hold.text}
											onChange={(e) => setHold({ ...hold, text: e.target.value })}
											className={SETTINGS_INPUT_CLASS}
										/>
									</Field>
								</>
							)}
						</SettingsCard>
					)}
				</SettingsSection>
			</SettingsBody>
			<SaveBar
				visible={isDirty}
				saving={setAutomationGuards.isPending}
				canSave={canEdit}
				onCancel={() => setHold(saved)}
				onSave={() => void handleSave()}
			/>
		</>
	);
}

/** Settings › Handling: what Kim does with a ticket it takes. `?tab=` opens a tab directly. */
export default function HandlingPage() {
	const [tab, setTab] = useTabParam(TABS, 'pre-processing');
	return (
		<SettingsPage title='Handling' tabs={TABS} tab={tab} onTabChange={setTab}>
			<TabsContent value='pre-processing' className='mt-0 flex flex-1 flex-col'>
				<TicketPreprocessingPage />
			</TabsContent>
			<TabsContent value='customer-facing' className='mt-0 flex flex-1 flex-col'>
				<CustomerFacingTab />
			</TabsContent>
		</SettingsPage>
	);
}
